import React, { useEffect, useState } from 'react';
import { getTodayReservationsByLocalIdAndDate } from '../../../../../../crud/clients_api/client_reservations';
import './DaySchedule.scss';
import ReservationModal from './components/ReservationModal/ReservationModal';
import MiniLoadingSpinner from '../../../../../../components/Spinners/MiniLoadingSpinner';
import { FaInfoCircle } from 'react-icons/fa';
import { useNotifications } from '../../../../../Base/components/NavBar/components/HeaderProfile/components/NotificationCenter/NotificationContext';

interface ReservationDetails {
  reservation_id: number;
  client_id: number | null;
  client_name: string;
  email: string;
  phone_number: string;
  reservation_date: string;
  local_id: number;
  init: number;
  end: number;
  n_people: number;
  mesa_id: number;
  mesa_num: number;
  description: string;
  confirmed: boolean;
  approved: boolean;
  attended: boolean;
  cancelled: boolean;
  arrived: boolean;
}

function slotToTime(slot: number) {
  const hours = Math.floor(slot / 4);
  const minutes = (slot % 4) * 15;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

interface DayScheduleProps {
  closingTime?: number;
}

const DaySchedule: React.FC<DayScheduleProps> = ({ closingTime = 95 }) => {
  const { reservations, setReservations } = useNotifications();
  const [todayReservations, setTodayReservations] = useState<ReservationDetails[]>([]);
  const [positioned, setPositioned] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [nowSlot, setNowSlot] = useState(0);
  const [modalRes, setModalRes] = useState<ReservationDetails | null>(null);
  const [showPast, setShowPast] = useState(false);

  const layout = (events: ReservationDetails[]) => {
    type E = ReservationDetails & { idx: number };
    const arr: E[] = events
      .map((e, i) => ({ ...e, idx: i }))
      .sort((a, b) => a.init - b.init);
    const cols: E[][] = [];
    const assign: { [idx: number]: { col: number } } = {};
    arr.forEach(ev => {
      let placed = false;
      for (let c = 0; c < cols.length; c++) {
        const last = cols[c][cols[c].length - 1];
        if (last.end <= ev.init) {
          cols[c].push(ev);
          assign[ev.idx] = { col: c };
          placed = true;
          break;
        }
      }
      if (!placed) {
        cols.push([ev]);
        assign[ev.idx] = { col: cols.length - 1 };
      }
    });
    const totalCols = cols.length;
    return events.map((e, i) => ({
      ...e,
      col: assign[i].col,
      totalCols,
    }));
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user.local_id;
    const today = new Date().toLocaleDateString('en-CA');
    getTodayReservationsByLocalIdAndDate(localId, today)
      .then(r => setReservations(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const today = new Date().toLocaleDateString('en-CA');
    const todayRes = reservations.filter(
      (res: ReservationDetails) => res.reservation_date === today
    );
    // Filtrar reservas: no mostrar si está approved, confirmed, arrived y attended
    const filteredRes = todayRes.filter(r => {
      if (r.approved && r.confirmed && r.arrived && r.attended) return false;
      return true;
    });
    setTodayReservations(filteredRes);
    setPositioned(layout(filteredRes.filter(r => !r.cancelled && !r.attended)));
  }, [reservations]);

  useEffect(() => {
    const update = () => {
      const d = new Date();
      setNowSlot(d.getHours() * 4 + Math.floor(d.getMinutes() / 15));
    };
    update();
    const iv = setInterval(update, 60_000);
    return () => clearInterval(iv);
  }, []);

  const handleCancelReservation = (reservationId: number) => {
    setReservations(prev => 
      prev.map(r => 
        r.reservation_id === reservationId ? { ...r, cancelled: true } : r
      )
    );
  };

  const handleUpdateReservation = (updated: ReservationDetails) => {
    setReservations(prev =>
      prev.map(r =>
        r.reservation_id === updated.reservation_id ? { ...r, ...updated } : r
      )
    );
  };

  const infoStatus = (confirmed: boolean, approved: boolean, arrived: boolean) => {
    if (confirmed && approved && arrived)
      return <span className="ds-res-meta attending">Atendiendo</span>;
    if (confirmed && approved)
      return <span className="ds-res-meta confirmed-approved">Reserva confirmada y aprobada</span>;
    if (confirmed && !approved)
      return <span className="ds-res-meta awaiting-approval">Reserva confirmada pero no aprobada</span>;
    return <span className="ds-res-meta awaiting-confirmation">Esperando confirmación</span>;
  };

  if (loading) return <MiniLoadingSpinner />;
  if (error) return <div className="ds-error">Error cargando reservas</div>;

  const startSlot = showPast ? 0 : Math.max(0, nowSlot - 5 * 4);
  // Calcular hora inicial y final en horas enteras
  const startHour = Math.floor(startSlot / 4);
  const endHour = Math.ceil((closingTime + 1) / 4);

  return (
    <div className="day-schedule">
      <div className="ds-grid">
        <div className="ds-times">
          {Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i).map(
            h => (
              <div key={h} className="ds-time-cell">
                {h.toString().padStart(2, '0')}:00
              </div>
            )
          )}
        </div>
        <div className="ds-lanes">
          <div className="ds-now-line" style={{ top: `${(nowSlot -
 startSlot) * 1}rem` }} />
          {positioned.map((r, idx) => {
            if (r.end < startSlot || r.init > closingTime) return null;
            const top = r.init - startSlot;
            const height = r.end - r.init;
            const width = 100 / r.totalCols;
            const left =
              idx > 0 && positioned[idx - 1].confirmed
                ? (width / 1.6) * r.col
                : (width / 2) * r.col;
            return (
              <div
                key={r.reservation_id}
                className="ds-reservation"
                style={{
                  top: `${top}rem`, 
                  height: `${height}rem`, 
                  width: `fit-content`, 
                  left: `calc(${left}% + 0.25rem)`
                }}
                onClick={() => setModalRes(r)}
              >
                <div className="ds-res-info">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span className="ds-res-client">{r.client_name}</span>
                  </div>
                  <span className="ds-res-meta">M#{r.mesa_num} · {r.n_people}p</span>
                  <span className="ds-res-time">{slotToTime(r.init)} - {slotToTime(r.end)}</span>
                  {infoStatus(r.confirmed, r.approved, r.arrived)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {modalRes && (
        <ReservationModal
          reservation={modalRes}
          show
          onClose={() => setModalRes(null)}
          onCancel={() => handleCancelReservation(modalRes.reservation_id)}
          onUpdateReservation={handleUpdateReservation}
        />
      )}
    </div>
  );
};

export default DaySchedule;
